import {MetricType} from 'common/types/reports/bookmark';
import {BehaviorEntity as Behavior} from 'app_api/projects/behaviors/types';
import {useBehaviors} from 'common/contexts/react/behaviors-context';
import {Translatable} from 'common/i18n';
import {BehaviorTypeIcons, OldMetricTypeIcons} from 'common/report/insights/util/metrics';

import {
  BehaviorMenuTypes,
  FunnelSectionItem,
  MenuType,
  MultiMetricsSection,
  RetentionSectionItem,
  SimpleSectionItem,
} from '../multi-metrics-menu/types';

function getBehaviorMenuItem(
  behavior: Behavior,
  metricType: `funnel` | `retention` | `simple`,
  firstClassMetricsEnabled: boolean,
): FunnelSectionItem | RetentionSectionItem | SimpleSectionItem {
  const iconMap = firstClassMetricsEnabled ? BehaviorTypeIcons : OldMetricTypeIcons;
  // Define a mapping of metric types to menu types and icons
  const metricTypeConfig: Record<string, {menuType: BehaviorMenuTypes; icon: string}> = {
    funnel: {menuType: MenuType.Funnel, icon: iconMap[MetricType.Funnel]},
    retention: {menuType: MenuType.Retention, icon: iconMap[MetricType.Retention]},
    simple: {menuType: MenuType.Simple, icon: iconMap[MetricType.Simple]},
  };

  // Get the configuration based on the metric type
  const config = metricTypeConfig[metricType];

  return {
    icon: config.icon,
    label: behavior.name,
    menuType: config.menuType,
    value: behavior,
  };
}

export function getSavedBehaviorSection({
  firstClassMetricsEnabled,
  isRecentSection = false,
  label,
  metricType,
  savedBehaviors,
}: {
  firstClassMetricsEnabled: boolean;
  isRecentSection?: boolean;
  label: Translatable;
  metricType: `funnel` | `retention` | `simple`;
  savedBehaviors: Behavior[];
}): MultiMetricsSection<Behavior> {
  return {
    label,
    isRecentSection,
    items: savedBehaviors.map((behavior) => getBehaviorMenuItem(behavior, metricType, firstClassMetricsEnabled)),
  };
}

export function getSavedBehaviorSections({
  firstClassMetricsEnabled,
  metricType,
  recentBehaviorMenuItems,
  savedBehaviors,
}: {
  firstClassMetricsEnabled: boolean;
  metricType: `funnel` | `retention` | `simple`;
  recentBehaviorMenuItems: Behavior[];
  savedBehaviors: Behavior[];
}): Array<MultiMetricsSection<Behavior>> {
  if (savedBehaviors.length) {
    const recentBehaviors = savedBehaviors.filter((behavior) =>
      recentBehaviorMenuItems.some((item) => item.id === behavior.id),
    );

    const showRecents = recentBehaviors.length > 6 && recentBehaviors.length > 0;
    return [
      /** only display a recently used section when there are enough items to fill in the dropdown */
      getSavedBehaviorSection({
        firstClassMetricsEnabled,
        isRecentSection: true,
        label: ({t}) => t(`query-builder.multi-metrics-menu.section-recents`),
        metricType,
        savedBehaviors: showRecents ? recentBehaviors : [],
      }),
      getSavedBehaviorSection({
        firstClassMetricsEnabled,
        label: ({t}) => t(`query-builder.multi-metrics-menu.section-${metricType}`),
        metricType,
        savedBehaviors,
      }),
    ].filter(Boolean);
  }

  return [];
}

export function useSavedBehaviors({metricType}: {metricType: `funnel` | `retention` | `simple`}): Behavior[] {
  const behaviors = useBehaviors();
  return (
    behaviors
      .getAllValues()
      // fresh deleted behaviors result in `null` in lexicon
      .filter(Boolean)
      .filter((metric) => metric.type === metricType && metric.can_view)
  );
}
